import { DEFAULT_TENANT_ID } from "../../config/app.config";

/**
 * Multi-token storage infrastructure (NG-8051).
 *
 * Backs auth tokens with `localStorage`, keyed by tenant id, so the browser can hold a
 * separate access/refresh pair per tenant:
 *
 *   { [tenantId]: { accessToken, refreshToken, accessTokenExpiry, refreshTokenExpiry } }
 *
 * No in-memory cache — every read pulls fresh from `localStorage`. `JSON.parse` is negligible
 * next to the network call that follows, and a fresh read keeps every tab naturally coherent for
 * the main store: a logout in one tab clears storage; other tabs hit 401 on their next request and
 * fall through the existing "no refresh token -> redirect to login" path. The assume-identity flow
 * is the one exception that needs a cross-tab `storage` listener (App.tsx, NG-8061): a released
 * assumed session must actively revert sibling tabs, not wait for their next 401.
 *
 * Two slots live in this module to avoid duplication: the main `tokenStore` and a separate
 * `assumedTokenStore` used by the Assume-Identity flow (NG-8061). Consumers pick the slot via
 * `getActiveStore()`, which keys off `sessionStorage.assumeMode`.
 */

export interface TokenEntry {
    accessToken: string;
    refreshToken: string;
    /** Absolute expiry as epoch milliseconds. */
    accessTokenExpiry: number;
    /** Absolute expiry as epoch milliseconds. */
    refreshTokenExpiry: number;
}

type TokenMap = Record<string, TokenEntry>;

export interface TokenStore {
    get(tenantId: number): TokenEntry | undefined;
    set(tenantId: number, entry: TokenEntry): void;
    remove(tenantId: number): void;
    clearAll(): void;
}

export const TOKEN_STORE_KEY = "th_tokens";
export const ASSUMED_TOKEN_STORE_KEY = "th_assumed_tokens";
// CS user's own tenant id, captured at assume-start. A cross-tab pointer back into the main
// `tokenStore` so any assume tab can revert to the CS user (NG-8061 multi-tab). localStorage, not
// sessionStorage, because sibling/reloaded assume tabs never received the CS context in-tab.
export const CS_TENANT_POINTER_KEY = "th_cs_tenant";

// TokenStore is the single owner of these per-tab routing keys. tenantContext.ts (NG-8055)
// re-exports and delegates to the helpers below rather than touching sessionStorage itself.
export const SESSION_STORAGE_TENANT_CONTEXT = "tenantContext";
export const SESSION_STORAGE_ASSUME_MODE = "assumeMode";

/** Reads the whole tenant->tokens map fresh. Missing/empty/corrupt storage is treated as `{}`. */
const readMap = (storageKey: string): TokenMap => {
    try {
        const raw = window.localStorage.getItem(storageKey);
        if (!raw) {
            return {};
        }
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === "object" ? (parsed as TokenMap) : {};
    } catch {
        // A throw here would break every getAccessToken() call, i.e. every request — fail soft.
        return {};
    }
};

const writeMap = (storageKey: string, map: TokenMap): void => {
    window.localStorage.setItem(storageKey, JSON.stringify(map));
};

export const createTokenStore = (storageKey: string): TokenStore => ({
    get(tenantId) {
        return readMap(storageKey)[String(tenantId)];
    },
    set(tenantId, entry) {
        const map = readMap(storageKey);
        map[String(tenantId)] = entry;
        writeMap(storageKey, map);
    },
    remove(tenantId) {
        const map = readMap(storageKey);
        delete map[String(tenantId)];
        writeMap(storageKey, map);
    },
    clearAll() {
        window.localStorage.removeItem(storageKey);
    },
});

/**
 * @private
 * do not use this to get access tokens - the correct is getActiveStore()
 */
export const tokenStore = createTokenStore(TOKEN_STORE_KEY);
/**
 * @private
 * do not use this to  get access tokens - the correct is getActiveStore()
 */
export const assumedTokenStore = createTokenStore(ASSUMED_TOKEN_STORE_KEY);

/** Converts a BE `expires_in`-style duration (seconds) into an absolute epoch-ms expiry. */
export const toAbsoluteExpiry = (seconds: number): number => Date.now() + seconds * 1000;

/** True when there is no expiry or it is at/past now. */
export const isExpired = (expiryMs: number): boolean => !expiryMs || expiryMs <= Date.now();

/**
 * Tenant id the access token was actually MINTED for, read from its `tenantId` JWT claim
 * (plaintext — unlike userId/organizationId it is not encrypted by the BE), or null when the
 * token is not a parseable JWT or carries no usable claim. No signature check — the token comes
 * from the auth server; this only answers "which slot does it belong to".
 *
 * Exists because the BE can mint a token for a DIFFERENT tenant than the one whose refresh token
 * was exchanged: the refresh grant stamps the user's persisted current-tenant context, which any
 * other tab/device switch moves. Slot writes must therefore trust the token over the tab context,
 * or a wrong-tenant token silently lands in (and poisons) another tenant's slot.
 */
export const getTenantIdFromAccessToken = (accessToken: string): number | null => {
    try {
        const payload = accessToken.split(".")[1];
        const claims = JSON.parse(window.atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
        const tenantId = Number(claims?.tenantId);
        return Number.isInteger(tenantId) && tenantId > 0 ? tenantId : null;
    } catch {
        return null;
    }
};

/**
 * Builds a token entry from BE durations, replicating the cookie path's refresh-expiry fallback:
 * when the BE omits a refresh expiry (e.g. registration passes 0) fall back to twice the access
 * lifetime, otherwise we'd persist an already-expired refresh token and log the user straight out.
 */
export const buildTokenEntry = (accessToken: string, refreshToken: string, accessTokenExpiry: number, refreshTokenExpiry: number): TokenEntry => ({
    accessToken,
    refreshToken: refreshToken || "",
    accessTokenExpiry: toAbsoluteExpiry(accessTokenExpiry),
    refreshTokenExpiry: toAbsoluteExpiry(refreshTokenExpiry > 0 ? refreshTokenExpiry : accessTokenExpiry * 2),
});

/**
 * Current tenant id from `sessionStorage.tenantContext`, or null when unset/invalid. Tenant ids
 * are positive integers; reject "0", NaN, floats, and junk so tokens are never keyed under an
 * invalid slot. Use this when the absence of a context must be distinguishable (e.g. the NG-8055
 * fallback chain); use `getCurrentTenantId` when a concrete id is required.
 */
export const getCurrentTenantIdOrNull = (): number | null => {
    const raw = window.sessionStorage.getItem(SESSION_STORAGE_TENANT_CONTEXT);
    const parsed = Number(raw);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

/** Current tenant id, falling back to the default tenant when unset/invalid. */
export const getCurrentTenantId = (): number => getCurrentTenantIdOrNull() ?? DEFAULT_TENANT_ID;

export const setCurrentTenantId = (tenantId: number): void => {
    window.sessionStorage.setItem(SESSION_STORAGE_TENANT_CONTEXT, String(tenantId));
};

export const clearCurrentTenantId = (): void => {
    window.sessionStorage.removeItem(SESSION_STORAGE_TENANT_CONTEXT);
};

/**
 * CS-context pointer (NG-8061 multi-tab). Stored as a bare tenant id, never tokens — the CS's tokens
 * already live in `tokenStore` under this tenant. Written at assume-start; read on revert so a tab
 * left in assume mode with an emptied assumed slot can route back to the CS user.
 */
export const setCsTenantPointer = (tenantId: number): void => {
    window.localStorage.setItem(CS_TENANT_POINTER_KEY, String(tenantId));
};

/** Read the CS-context pointer, or null when unset/invalid. */
export const getCsTenantPointer = (): number | null => {
    const raw = window.localStorage.getItem(CS_TENANT_POINTER_KEY);
    const parsed = Number(raw);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

export const clearCsTenantPointer = (): void => {
    window.localStorage.removeItem(CS_TENANT_POINTER_KEY);
};

/**
 * True when the MAIN store holds a token entry for the tenant. Lets revert routing decide
 * CS-restore vs login without going through `getActiveStore()`'s assume-mode branch, and keeps app
 * code from touching `tokenStore` directly.
 */
export const hasMainStoreToken = (tenantId: number): boolean => tokenStore.get(tenantId) !== undefined;

// Strict match: the flag is only ever written as the literal "true" (or removed). A truthy check
// would wrongly treat the string "false" as assume mode.
export const isAssumeMode = (): boolean => window.sessionStorage.getItem(SESSION_STORAGE_ASSUME_MODE) === "true";

/**
 * Sole writer for the per-tab assume flag, paired with `isAssumeMode`'s reader. Removing the key
 * for `false` (rather than writing the string "false") keeps `isAssumeMode`'s truthy check honest.
 */
export const setAssumeMode = (on: boolean): void => {
    if (on) {
        window.sessionStorage.setItem(SESSION_STORAGE_ASSUME_MODE, "true");
    } else {
        window.sessionStorage.removeItem(SESSION_STORAGE_ASSUME_MODE);
    }
};

/** Single chokepoint for slot selection so every consumer reads/writes the same store. */
export const getActiveStore = (): TokenStore => (isAssumeMode() ? assumedTokenStore : tokenStore);

/**
 * Seed the assumed-identity slot from OUTSIDE assume mode — the one sanctioned write to the
 * assumed store that bypasses `getActiveStore()`. Used when a tab in normal mode (e.g. the CS
 * tab) provisions the assumed user's tokens (NG-8061). App code must not reach into
 * `assumedTokenStore` directly; go through this.
 */
export const seedAssumedToken = (tenantId: number, entry: TokenEntry): void => assumedTokenStore.set(tenantId, entry);

/** Remove just THIS identity's assumed-token slot on release/restore (NG-8061), keyed by the tab's
 *  current tenant. Assume tabs on OTHER tenants keep their slots; same-tenant siblings are reverted
 *  to the CS user by the App.tsx `storage` listener that this removal triggers. */
export const removeActiveAssumedToken = (): void => {
    const tenantId = getCurrentTenantIdOrNull();
    if (tenantId !== null) {
        assumedTokenStore.remove(tenantId);
    }
};

/** Combined logout clear: wipes both slots and the session-scoped routing keys. */
export const clearAllStores = (): void => {
    tokenStore.clearAll();
    assumedTokenStore.clearAll();
    clearCsTenantPointer();
    window.sessionStorage.removeItem(SESSION_STORAGE_TENANT_CONTEXT);
    window.sessionStorage.removeItem(SESSION_STORAGE_ASSUME_MODE);
};
